// src/utils/notify.js
//
// Lightweight helpers for two kinds of "hey, pay attention" signals used
// by the support system:
//
//   1. `playChime()` — a short, soft tone generated on the fly with the
//      Web Audio API. No asset files to ship, no autoplay-blocked mp3s.
//      Rate-limited to one chime per 5 seconds so a burst of messages
//      (e.g. multiple replies in rapid succession) doesn't turn into a
//      noisy alarm.
//
//   2. `requestNotificationPermission()` / `showNotification()` — wraps
//      the standard Notification API with forgiving fallbacks so the
//      caller never has to branch on `typeof Notification`. Safe to
//      call anywhere; a no-op in unsupported or denied environments.
//
// Both functions deliberately never throw: this is user-feedback glue,
// not something the app should break over if the browser is weird.

let lastChimeAt = 0;

// Cache a single AudioContext for the life of the tab. Creating one per
// chime is fine but wasteful, and some browsers (Safari) also frown on
// rapid context churn. Constructed lazily on first call so SSR and
// test environments don't trip over `window`.
let sharedCtx = null;

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!sharedCtx) {
    try { sharedCtx = new AC(); } catch { return null; }
  }
  return sharedCtx;
}

// Play a soft two-tone chime. `minGapMs` guards against runaway
// triggers; callers can override for unit-test scenarios but the
// default 5s is the product-level rate limit.
export function playChime({ minGapMs = 5000 } = {}) {
  try {
    const now = Date.now();
    if (now - lastChimeAt < minGapMs) return;
    const ctx = getAudioContext();
    if (!ctx) return;
    // Some browsers create the context in "suspended" state until a
    // user gesture. Resume is idempotent; ignore rejection.
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    const startAt = ctx.currentTime;
    const duration = 0.35;

    // Two quick sine notes (C5 -> E5) with a gentle exponential decay.
    // Pleasant and unobtrusive — closer to a Mac "ding" than a pager.
    const notes = [
      { freq: 523.25, offset: 0.0 }, // C5
      { freq: 659.25, offset: 0.12 }, // E5
    ];
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.connect(ctx.destination);

    notes.forEach(({ freq, offset }) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startAt + offset);
      osc.connect(gain);
      const noteStart = startAt + offset;
      const noteEnd = noteStart + duration;
      gain.gain.setValueAtTime(0.0001, noteStart);
      gain.gain.exponentialRampToValueAtTime(0.18, noteStart + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, noteEnd);
      osc.start(noteStart);
      osc.stop(noteEnd + 0.02);
    });
    lastChimeAt = now;
  } catch {
    // No-op: audio is a nice-to-have.
  }
}

// Ask the user once. Subsequent calls are cheap no-ops because the
// browser caches the decision. Returns a Promise that resolves to the
// current permission string ('granted' | 'denied' | 'default').
export function requestNotificationPermission() {
  try {
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
      return Promise.resolve('unsupported');
    }
    const current = window.Notification.permission;
    if (current === 'granted' || current === 'denied') {
      return Promise.resolve(current);
    }
    return window.Notification.requestPermission().catch(() => 'default');
  } catch {
    return Promise.resolve('default');
  }
}

// Show a desktop notification if we have permission. Focuses the tab
// when clicked (most browsers require the tab to be non-foreground for
// the notification to surface visually, but clicking should still
// bring the window forward).
export function showNotification(title, { body, tag, onClick } = {}) {
  try {
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') return null;
    if (window.Notification.permission !== 'granted') return null;
    // Skip if the user is actively looking at the tab — the in-app
    // badges and chime are enough; a system notification on top is
    // just noise for someone already reading the chat.
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return null;
    const n = new window.Notification(title, {
      body: body || '',
      tag: tag || 'modality-support',
      // Renotify so repeat messages with the same tag still surface
      // instead of silently replacing the previous one.
      renotify: true,
      silent: true, // We already play our own chime; avoid doubled sounds.
    });
    n.onclick = () => {
      try {
        window.focus();
        if (typeof onClick === 'function') onClick();
      } catch {}
      n.close();
    };
    return n;
  } catch {
    return null;
  }
}
